import React, { useCallback, useContext, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { graphql } from "react-relay";
import TextArea from "react-textarea-autosize";

import { faClock, faCommentAltLines, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { debounce } from "lodash";
import moment, { type Moment } from "moment";

import { DueDate } from "~/common";
import type { TeamType, UserType } from "~/common/AssigneeFilter";
import ViewportContext from "~/common/ViewportContext";
import { ReactComponent as DraggableGrip } from "~/legacy-assets/images/icons/draggable-grip.svg";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { DueDateSelect } from "~/notes/AssignableDropdown";
import AssigneeSelector from "~/okrs/components/AssigneeSelector";
import TargetInput from "~/okrs/components/TargetInput";
import WeightInput from "~/okrs/components/WeightInput";
import { OKRsContext } from "~/okrs/ObjectiveList";
import { Button, useModals } from "~/ui";
import IconButton from "~/ui/button/IconButton";
import { LabelIconForInlineButton } from "~/ui/button/InlineButton";
import Menu from "~/ui/menu";
import type { SelectorOption } from "~/ui/selectors/Selector";

import { KeyResultDeleteModal } from "./components/KeyResultDeleteModal";

import type {
	KeyResultCreateOrEditMutation,
	KeyResultMutationInput,
	MetricProgress,
	MetricType,
} from "~/__generated__/KeyResultCreateOrEditMutation.graphql";
import type { ObjectiveQuery_Info$data as ObjectiveQueryInfo } from "~/__generated__/ObjectiveQuery_Info.graphql";

import stylesObjective from "~/okrs/Objective.module.css";
import stylesObjectiveList from "~/okrs/ObjectiveList.module.css";

import styles from "./KeyResult.module.css";

export type KeyResultAttributes = NonNullable<ObjectiveQueryInfo["keyResults"]>[0];

interface KeyResultProps extends KeyResultAttributes {
	objectiveId: string;
	archived: boolean;
	hasAccess: boolean;
	innerRef?: React.Ref<HTMLDivElement>;
	className?: string;
	onTitleChange?: (newTitle: string) => void;
	onDueDateChange?: (newDueDate: Moment | null) => void;
	onAssigneeChange?: (newUser: UserType) => void;
	onAssigneeTeamChange?: (newTeam: TeamType) => void;
	onMetricChange?: (
		newMetricProgress: MetricProgress,
		newMetricType: MetricType,
		newMetricCurrency: string | undefined,
		newMetricStart: number,
		newMetricCurrent: number,
		newMetricTarget: number,
	) => void;
	onWeightChange?: (newWeight: number) => void;
	onDelete?: () => void;
	setIsAddingDescription?: (isAddingDescription: boolean) => void;
	setSelectedOkr?: (selectedOkr: SelectorOption) => void;
	onChangeToUpdatesView?: () => void;
}

export const MAX_TITLE_LENGTH = 1024;
const TITLE_ERROR_MESSAGE = `Key Result title can't be longer than ${MAX_TITLE_LENGTH} Characters`;

export const keyResultCreateOrEditMutation = graphql`
	mutation KeyResultCreateOrEditMutation($keyResult: KeyResultMutationInput!) {
		KeyResult_CreateEdit(keyResult: $keyResult) {
			keyResult {
				id
				title
				dueDate
				assigneeUser {
					id
					email
					image
					fullName
					isGuest
				}
				assigneeTeam {
					id
					title
				}
				metricProgress
				metricType
				metricCurrency
				metricStart
				metricCurrent
				metricTarget
				weight
				order
				progress
			}
		}
	}
`;

export const keyResultDeleteMutation = graphql`
	mutation KeyResultDeleteMutation($keyResultId: ID!) {
		KeyResult_Delete(keyResultId: $keyResultId) {
			ok
		}
	}
`;

export const keyResultReorderMutation = graphql`
	mutation KeyResultReorderMutation($objective: ID!, $oldIndex: Int!, $newIndex: Int!) {
		KeyResult_Reorder(objectiveId: $objective, oldIndex: $oldIndex, newIndex: $newIndex) {
			objective {
				id
				keyResults {
					id
					order
				}
			}
		}
	}
`;

export default function KeyResult({
	id,
	title,
	dueDate,
	assigneeUser,
	assigneeTeam,
	metricProgress,
	metricType,
	metricCurrency,
	metricStart,
	metricCurrent,
	metricTarget,
	weight,
	progress,
	order,
	objectiveId,
	archived,
	hasAccess,
	innerRef,
	className,
	onTitleChange: parentOnTitleChange,
	onDueDateChange: parentOnDueDateChange,
	onAssigneeChange: parentOnAssigneeChange,
	onAssigneeTeamChange: parentOnAssigneeTeamChange,
	onMetricChange: parentOnMetricChange,
	onWeightChange: parentOnWeightChange,
	onDelete: parentOnDelete,
	setIsAddingDescription,
	setSelectedOkr,
	onChangeToUpdatesView,
	...restProps
}: KeyResultProps) {
	const { createView, editView, setIsLoading } = useContext(OKRsContext);
	const { isMobile } = useContext(ViewportContext);
	const [dayPickerOpened, setDayPickerOpened] = useState<boolean>(false);
	const [inError, setInError] = useState<boolean>(false);
	const [errorList, setErrorList] = useState<string[]>([]);
	const modals = useModals();

	// We are updating some values using debounce, a user may start changing a field
	// And before the debounce fires starts to edit another field that also contains debounce
	// For those cases we will store the values that are "scheduled" to update in this state.
	const [debounceUpdateValues, setDebounceUpdateValues] = useState<{
		title?: string;
		metricProgress?: MetricProgress;
		metricType?: MetricType;
		metricCurrency?: string;
		metricStart?: number;
		metricCurrent?: number;
		metricTarget?: number;
	}>({});

	const stopPropagation = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
	}, []);

	const openDayPicker = useCallback(
		(e: React.MouseEvent) => {
			stopPropagation(e);
			setDayPickerOpened(true);
		},
		[stopPropagation],
	);

	const closeDayPicker = useCallback(() => {
		setDayPickerOpened(false);
	}, [setDayPickerOpened]);

	const onDatePickerInsideClick = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
	}, []);

	const onEditKeyResult = useCallback(
		(editedFields: Omit<Partial<KeyResultMutationInput>, "id" | "progress">) => {
			setIsLoading?.(true);
			commitMutationPromise<KeyResultCreateOrEditMutation>(environment, {
				mutation: keyResultCreateOrEditMutation,
				variables: {
					keyResult: {
						id,
						objective: objectiveId,
						...editedFields,
					},
				},
				optimisticUpdater: store => {
					const node = store.get(id);
					if (!node) {
						return;
					}
					for (const [key, value] of Object.entries(editedFields)) {
						if (["assigneeTeam", "assigneeUser"].includes(key) && value) {
							const assigneeNode = store.get(value as string);
							if (assigneeNode) node.setLinkedRecord(assigneeNode, key);
						} else {
							// @ts-ignore
							node.setValue(value, key);
						}
					}
				},
				onCompleted: () => {
					setIsLoading?.(false);
				},
				onError: e => {
					setIsLoading?.(false);
					// TODO: Show error somewhere
					console.log(e);
				},
			});
		},
		[id, objectiveId, setIsLoading],
	);

	const debouncedOnEditKeyResult = useMemo(
		() =>
			debounce(updateValues => {
				onEditKeyResult(updateValues);
				// Reset debounce update values once debounce triggers.
				setDebounceUpdateValues({});
			}, 500),
		[onEditKeyResult],
	);

	const verifyAndAddError = useCallback(
		(error: string) => {
			if (errorList.includes(error)) return;
			setErrorList([...errorList, error]);
			setInError(true);
		},
		[errorList],
	);

	const removeError = useCallback(
		(error: string) => {
			setErrorList(errorList.filter(e => e !== error));
		},
		[errorList],
	);

	const onTitleChange = useCallback(
		(newTitle: string) => {
			parentOnTitleChange?.(newTitle);

			const updateValues = { ...debounceUpdateValues, title: newTitle };

			if (newTitle.length > MAX_TITLE_LENGTH) {
				verifyAndAddError(TITLE_ERROR_MESSAGE);
				return;
			} else {
				removeError(TITLE_ERROR_MESSAGE);
				setInError(false);
			}

			if (!id) {
				return;
			}

			setDebounceUpdateValues(updateValues);
			debouncedOnEditKeyResult(updateValues);
		},
		[
			parentOnTitleChange,
			id,
			debounceUpdateValues,
			setDebounceUpdateValues,
			debouncedOnEditKeyResult,
			removeError,
			verifyAndAddError,
		],
	);

	const onTitleChangeEvent = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (!event.target) {
				return;
			}
			onTitleChange(event.target.value);
		},
		[onTitleChange],
	);

	const onDueDateChange = useCallback(
		(newDueDate: Moment) => {
			closeDayPicker();
			parentOnDueDateChange?.(newDueDate);
			if (!id) {
				return;
			}
			onEditKeyResult({ dueDate: newDueDate.format("YYYY-MM-DD") });
		},
		[closeDayPicker, parentOnDueDateChange, id, onEditKeyResult],
	);

	const onDueDateRemoved = useCallback(() => {
		closeDayPicker();
		parentOnDueDateChange?.(null);
		if (!id) {
			return;
		}
		onEditKeyResult({ dueDate: null });
	}, [closeDayPicker, parentOnDueDateChange, id, onEditKeyResult]);

	const onMetricsChange = useCallback(
		(
			newMetricProgress: MetricProgress,
			newMetricType: MetricType,
			newMetricCurrency: string | undefined,
			newMetricStart: number,
			newMetricCurrent: number,
			newMetricTarget: number,
		) => {
			parentOnMetricChange?.(
				newMetricProgress,
				newMetricType,
				newMetricCurrency,
				newMetricStart,
				newMetricCurrent,
				newMetricTarget,
			);
			if (!id) {
				return;
			}
			const updateValues = {
				...debounceUpdateValues,
				metricProgress: newMetricProgress,
				metricType: newMetricType,
				metricCurrency: newMetricCurrency,
				metricStart: newMetricStart,
				metricCurrent: newMetricCurrent,
				metricTarget: newMetricTarget,
			};
			setDebounceUpdateValues(updateValues);
			debouncedOnEditKeyResult(updateValues);
		},
		[parentOnMetricChange, id, debounceUpdateValues, setDebounceUpdateValues, debouncedOnEditKeyResult],
	);

	const onWeightChange = useCallback(
		(newWeight: number) => {
			parentOnWeightChange?.(newWeight);
			if (!id) {
				return;
			}
			onEditKeyResult({ weight: newWeight });
		},
		[parentOnWeightChange, id, onEditKeyResult],
	);

	const onAssigneeUserChange = useCallback(
		(newAssignee: UserType) => {
			parentOnAssigneeChange?.(newAssignee);
			if (id) onEditKeyResult({ assigneeUser: newAssignee.id, assigneeTeam: null });
		},
		[parentOnAssigneeChange, id, onEditKeyResult],
	);

	const onAssigneeTeamChange = useCallback(
		(newAssignee: TeamType) => {
			parentOnAssigneeTeamChange?.(newAssignee);
			if (id) onEditKeyResult({ assigneeTeam: newAssignee.id, assigneeUser: null });
		},
		[parentOnAssigneeTeamChange, id, onEditKeyResult],
	);

	const onDelete = useCallback(async () => {
		const confirmDelete = await modals.open<boolean>(({ close }) => (
			<KeyResultDeleteModal close={close} keyResultTitle={title} />
		));

		if (!confirmDelete) return;

		if (parentOnDelete && confirmDelete) {
			return parentOnDelete();
		}
	}, [parentOnDelete, modals, title]);

	const addCommentToKeyResult = useCallback(() => {
		if (setIsAddingDescription && setSelectedOkr && onChangeToUpdatesView) {
			setIsAddingDescription(true);
			setSelectedOkr({ label: title, value: id });
			onChangeToUpdatesView();
		}
	}, [id, setIsAddingDescription, setSelectedOkr, onChangeToUpdatesView, title]);

	let progressClass = styles.progressGreen;
	if (progress <= 30) {
		progressClass = styles.progressRed;
	} else if (progress <= 60) {
		progressClass = styles.progressYellow;
	}

	// The key result style used in modals
	if (createView || editView) {
		return (
			<div
				className={cx(styles.keyResultData, stylesObjective.keyResultData, className, {
					[cx(styles.archived, stylesObjective.archived)]: archived,
					[styles.createOrEditView]: createView || editView,
					[styles.errorState]: inError,
				})}
				ref={innerRef}
				{...restProps}
			>
				<div className={cx(styles.title, stylesObjectiveList.title, { [stylesObjective.untitled]: !title })}>
					{hasAccess && (
						<div className={styles.grip}>
							<DraggableGrip fill="currentColor" width="16" height="10" viewBox="0 0 6 10" />
						</div>
					)}
					<span className={styles.order}>{`${order + 1}. `}</span>
					{hasAccess ? (
						<>
							<TextArea value={title} minRows={1} onChange={onTitleChangeEvent} placeholder="Key result title" />
							<Menu>
								<Menu.Trigger>
									<IconButton variant="tertiary" icon={faEllipsisV} />
								</Menu.Trigger>
								<Menu.Content align="end" side="bottom">
									{setIsAddingDescription && setSelectedOkr && (
										<>
											<Menu.Item onSelect={addCommentToKeyResult}>
												<LabelIconForInlineButton icon={faCommentAltLines} side="left" />
												Add a comment
											</Menu.Item>
											<Menu.Separator />
										</>
									)}
									<Menu.Item onSelect={onDelete} destructive>
										<LabelIconForInlineButton icon={faTrashCan} side="left" />
										Delete key result
									</Menu.Item>
								</Menu.Content>
							</Menu>
						</>
					) : (
						title || "Untitled Key Result"
					)}
				</div>
				{inError && errorList.length > 0 && (
					<ul className={styles.errorList}>
						{errorList.map(error => {
							let errorMessage = error;
							if (error === TITLE_ERROR_MESSAGE) {
								errorMessage += ` (${title.length}/${MAX_TITLE_LENGTH}).`;
							}
							return (
								<li className={styles.errorListItem} key={error}>
									{errorMessage}
								</li>
							);
						})}
					</ul>
				)}
				<div className={styles.data}>
					<div className={cx(styles.assignedTo, stylesObjective.assignedTo, stylesObjectiveList.assignedTo)}>
						<AssigneeSelector
							user={assigneeUser}
							team={assigneeTeam}
							setUser={onAssigneeUserChange}
							setTeam={onAssigneeTeamChange}
							readOnly={!hasAccess}
							avatarOnly={isMobile}
						/>
					</div>
					<div className={cx(styles.dueDate, stylesObjective.dueDate, stylesObjectiveList.dueDate)}>
						{dueDate ? (
							<DueDate
								as={Button}
								short
								done={progress >= 100}
								date={dueDate as string | undefined}
								className={cx("time", stylesObjective.dueDateTime, stylesObjective.dataButton, {
									[stylesObjective.dueDateDone]: progress >= 100,
									[stylesObjective.dataButtonDisabled]: !hasAccess,
								})}
								onClick={openDayPicker}
							/>
						) : (
							<IconButton
								icon={faClock}
								className={cx(stylesObjective.dueDateClock, stylesObjective.dataButton, {
									[stylesObjective.dataButtonDisabled]: !hasAccess,
								})}
								onClick={openDayPicker}
							/>
						)}
						{dayPickerOpened && (
							<OutsideClickHandler onOutsideClick={closeDayPicker}>
								<div className={cx("date-picker", stylesObjective.datePicker)} onClickCapture={onDatePickerInsideClick}>
									<DueDateSelect
										date={dueDate ? moment(dueDate) : undefined}
										updateDate={onDueDateChange}
										removeDate={onDueDateRemoved}
										goBack={closeDayPicker}
									/>
								</div>
							</OutsideClickHandler>
						)}
					</div>
					<div className={cx(styles.status, stylesObjective.status, stylesObjectiveList.status)}>
						<TargetInput
							progress={metricProgress}
							type={metricType}
							currency={metricCurrency || undefined}
							start={metricStart}
							current={metricCurrent}
							target={metricTarget}
							onSubmit={onMetricsChange}
							className={cx(styles.status, stylesObjective.status, stylesObjectiveList.status)}
							hasAccess={hasAccess}
							isCreateView={createView}
						/>
					</div>
					<div className={cx(styles.progress, stylesObjective.progress, stylesObjectiveList.progress, progressClass)}>
						{!hasAccess ? (
							weight > 1 && <span>{weight}x</span>
						) : (
							<WeightInput weight={weight} onChange={onWeightChange} isKeyResult className={styles.weightSelection} />
						)}
						{progress}%
					</div>
				</div>
			</div>
		);
	}

	if (isMobile) {
		return (
			<div
				className={cx(styles.keyResultData, stylesObjective.keyResultData, className, {
					[cx(styles.archived, stylesObjective.archived)]: archived,
				})}
			>
				<div>
					<div className={cx(styles.title, stylesObjectiveList.title)}>
						<span className={styles.order}>{`${order + 1}. `}</span>
					</div>
				</div>
				<div className={styles.titleColumn}>
					<div
						className={cx(styles.title, stylesObjectiveList.title, styles.titleText, {
							[stylesObjective.untitled]: !title,
						})}
					>
						{title || "Untitled Key Result"}
					</div>
					<div className={cx(styles.assignedTo, stylesObjective.assignedTo, stylesObjectiveList.assignedTo)}>
						<AssigneeSelector
							user={assigneeUser}
							team={assigneeTeam}
							setUser={onAssigneeUserChange}
							setTeam={onAssigneeTeamChange}
							readOnly
						/>
					</div>
				</div>
				<div>
					<div className={cx(styles.progress, stylesObjective.progress, stylesObjectiveList.progress, progressClass)}>
						{weight > 1 && <span>{weight}x</span>}
						{progress}%
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cx(styles.keyResultData, stylesObjective.keyResultData, className, {
				[cx(styles.archived, stylesObjective.archived)]: archived,
			})}
			ref={innerRef}
			{...restProps}
		>
			<div className={cx(styles.title, stylesObjectiveList.title, { [stylesObjective.untitled]: !title })}>
				<span className={styles.order}>{`${order + 1}. `}</span>
				<span className={styles.titleText} title={title || "Untitled Key Result"}>
					{title || "Untitled Key Result"}
				</span>
			</div>
			<div className={cx(stylesObjective.dueDate, stylesObjectiveList.dueDate)}>
				{dueDate ? (
					<DueDate
						as={Button}
						short
						done={progress >= 100}
						date={dueDate as string | undefined}
						className={cx("time", stylesObjective.dueDateTime, stylesObjective.dataButton, {
							[stylesObjective.dueDateDone]: progress >= 100,
							[stylesObjective.dataButtonDisabled]: !hasAccess,
						})}
						onClick={openDayPicker}
					/>
				) : (
					<IconButton
						icon={faClock}
						className={cx(stylesObjective.dueDateClock, stylesObjective.dataButton, {
							[stylesObjective.dataButtonDisabled]: !hasAccess,
						})}
						onClick={openDayPicker}
					/>
				)}
				{dayPickerOpened && (
					<OutsideClickHandler onOutsideClick={closeDayPicker}>
						<div className={cx("date-picker", stylesObjective.datePicker)} onClickCapture={onDatePickerInsideClick}>
							<DueDateSelect
								date={dueDate ? moment(dueDate) : undefined}
								updateDate={onDueDateChange}
								removeDate={onDueDateRemoved}
								goBack={closeDayPicker}
							/>
						</div>
					</OutsideClickHandler>
				)}
			</div>
			<div
				className={cx(styles.assignedTo, stylesObjective.assignedTo, stylesObjectiveList.assignedTo)}
				onClick={stopPropagation}
			>
				<AssigneeSelector
					user={assigneeUser}
					team={assigneeTeam}
					setUser={onAssigneeUserChange}
					setTeam={onAssigneeTeamChange}
					readOnly={!hasAccess}
				/>
			</div>
			<div className={cx(styles.status, stylesObjective.status, stylesObjectiveList.status)} onClick={stopPropagation}>
				<TargetInput
					progress={metricProgress}
					type={metricType}
					currency={metricCurrency || undefined}
					start={metricStart}
					current={metricCurrent}
					target={metricTarget}
					onSubmit={onMetricsChange}
					className={cx(styles.status, stylesObjective.status, stylesObjectiveList.status)}
					hasAccess={hasAccess}
				/>
			</div>
			<div className={cx(styles.progress, stylesObjective.progress, stylesObjectiveList.progress, progressClass)}>
				{weight !== 1 && <span>{weight}x</span>}
				{progress}%
			</div>
			<div className={stylesObjectiveList.goToParent} />
		</div>
	);
}
